import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, Check, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showErrorToast, showSuccessToast } from '@/utils/toastUtils';
import { debugLogger } from '@/utils/debugLogger';

interface SelfieVerificationCardProps {
  verification: any;
  onUpdate: () => void;
}

const SelfieVerificationCard = ({ verification, onUpdate }: SelfieVerificationCardProps) => {
  const { user, profileData } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Check if email is verified to enable selfie
  const isEmailVerified = verification?.email_verified === true;

  const startCamera = async () => {
    if (isStarting || stream) return;
    setIsStarting(true);
    
    try {
      debugLogger.debug('camera', 'starting camera');
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'user' } },
        audio: false
      });

      const video = videoRef.current;
      if (!video) {
        setStream(mediaStream);
        setIsStarting(false);
        return;
      }

      // Configure iOS-friendly attributes
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      video.playsInline = true;
      video.controls = false;

      // Set stream
      video.srcObject = mediaStream;

      // Wait for metadata before playing
      await new Promise<void>((resolve) => {
        if (video.readyState >= 1) return resolve();
        const onLoaded = () => {
          video.removeEventListener('loadedmetadata', onLoaded);
          resolve();
        };
        video.addEventListener('loadedmetadata', onLoaded, { once: true });
      });

      await video.play();

      setStream(mediaStream);
      setIsCapturing(true);
      setIsRetrying(false); // Reset retry state after successful camera start
      
      debugLogger.debug('camera', 'camera started successfully', {
        width: video.videoWidth,
        height: video.videoHeight,
        readyState: video.readyState
      });
    } catch (err) {
      debugLogger.error('[SelfieCamera] startCamera failed', err);
      setIsRetrying(false); // Reset retry state on error
      showErrorToast({
        title: 'Erro na câmera',
        description: 'Não foi possível iniciar a câmera. Verifique permissões e tente novamente.'
      });
    } finally {
      setIsStarting(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => {
        try { 
          track.stop(); 
        } catch (e) {
          debugLogger.warn('[SelfieCamera] error stopping track', e);
        }
      });
    }
    setStream(null);
    setIsCapturing(false);

    const video = videoRef.current;
    if (video) {
      video.srcObject = null;
    }
  };

  useEffect(() => {
    return () => {
      try { 
        stopCamera(); 
      } catch (e) {
        debugLogger.warn('[SelfieCamera] cleanup error', e);
      }
    };
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const w = video.videoWidth || 720;
    const h = video.videoHeight || 1280;
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, w, h);

    // Check for black frame
    try {
      const px = ctx.getImageData(Math.floor(w/2), Math.floor(h/2), 1, 1).data;
      const blackish = px[0] < 5 && px[1] < 5 && px[2] < 5;
      if (blackish) {
        debugLogger.warn('[SelfieCamera] captured frame appears blackish', { px });
      }
    } catch (e) {
      debugLogger.warn('[SelfieCamera] getImageData failed', e);
    }

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(dataUrl);
    stopCamera();
    
    debugLogger.debug('camera', 'photo captured', { 
      dataUrlLength: dataUrl.length, 
      width: w, 
      height: h 
    });
  };

  const submitSelfie = async () => {
    if (!capturedImage || !verification?.id || !user?.id) {
      debugLogger.warn('[SelfieCamera] submitSelfie aborted: missing data', { 
        hasCapturedImage: !!capturedImage, 
        verificationId: verification?.id,
        userId: user?.id 
      });
      showErrorToast({
        title: 'Erro',
        description: 'Dados de autenticação ausentes. Por favor, faça login novamente.',
      });
      return;
    }

    setIsProcessing(true);
    debugLogger.debug('camera', 'submitting selfie', { 
      verificationId: verification.id,
      userId: user.id 
    });

    try {
      // Convert base64 to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();

      // Upload to storage with correct path structure: userId/selfie/filename.jpg
      const fileName = `selfie_${verification.id}_${Date.now()}.jpg`;
      const filePath = `${user.id}/selfie/${fileName}`;
      
      debugLogger.debug('camera', 'uploading to path', { filePath });
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('verification-documents')
        .upload(filePath, blob, {
          contentType: 'image/jpeg',
          upsert: false,
        });

      if (uploadError) {
        debugLogger.error('[SelfieCamera] upload error', uploadError);
        if (uploadError.message?.includes('row-level security') || uploadError.message?.includes('policy')) {
          showErrorToast({
            title: 'Erro de permissão',
            description: 'Erro ao fazer upload da selfie. Verifique suas permissões e tente novamente.',
          });
        }
        throw uploadError;
      }
      
      debugLogger.debug('camera', 'upload successful', { path: uploadData.path });

      // Analyze with AI
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke('analyze-selfie', {
        body: {
          imageBase64: capturedImage.split(',')[1],
          userProfile: {
            birth_date: profileData?.birth_date,
            gender: profileData?.gender,
          },
        },
      });

      if (analysisError) {
        debugLogger.error('[SelfieCamera] analysis error', analysisError);
        throw analysisError;
      }

      // Update verification record
      const { error: updateError } = await supabase
        .from('document_verifications')
        .update({
          selfie_photo_url: uploadData.path,
          ai_analysis: analysisData,
          selfie_verified: analysisData.approved,
          selfie_verified_at: analysisData.approved ? new Date().toISOString() : null,
        })
        .eq('id', verification.id);

      if (updateError) {
        debugLogger.error('[SelfieCamera] update error', updateError);
        throw updateError;
      }

      if (analysisData.approved) {
        showSuccessToast({
          title: 'Selfie Verificada!',
          description: 'Sua selfie foi aprovada com sucesso.',
        });
      } else {
        showErrorToast({
          title: 'Selfie não aprovada',
          description: analysisData.reason || 'Por favor, tire uma nova foto seguindo as instruções.',
        });
      }

      setCapturedImage(null);
      onUpdate();
      
      debugLogger.debug('camera', 'submission complete');

      // Send transactional email notification
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (userData?.user?.email) {
          await supabase.functions.invoke('send-transactional-email', {
            body: {
              templateKey: 'verification-submitted',
              toEmail: userData.user.email,
              variables: {
                Name: profileData?.username || 'Usuário',
              }
            }
          });
          debugLogger.debug('camera', 'verification email sent');
        }
      } catch (emailError) {
        // Don't fail the main operation if email fails
        debugLogger.warn('camera', 'failed to send verification email', emailError);
      }
    } catch (error) {
      debugLogger.error('[SelfieCamera] submission error', error);
      showErrorToast({
        title: 'Erro no processamento',
        description: 'Ocorreu um erro ao processar sua selfie. Tente novamente.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const retryRejectedSelfie = async () => {
    // Set retry state to force video element to render
    setIsRetrying(true);
    
    // Reset local states
    setCapturedImage(null);
    
    // NÃO limpar selfie_photo_url para permitir visualização da foto rejeitada
    try {
      await supabase
        .from('document_verifications')
        .update({
          ai_analysis: null,
          selfie_verified: false,
          selfie_verified_at: null,
          // selfie_photo_url permanece intacto para comparação
        })
        .eq('id', verification.id);
      
      onUpdate();
      
      // Wait for DOM update before starting camera
      setTimeout(() => {
        startCamera();
      }, 100);
    } catch (error) {
      debugLogger.error('[SelfieCamera] error resetting verification', error);
      setIsRetrying(false);
    }
  };

  const getCardStatus = () => {
    if (verification?.selfie_verified) return 'approved';
    if (verification?.ai_analysis && !verification?.selfie_verified) return 'rejected';
    if (isProcessing) return 'processing';
    return 'pending';
  };

  const status = getCardStatus();

  const getStatusIcon = () => {
    switch (status) {
      case 'approved': return <Check className="w-4 h-4" />;
      case 'rejected': return <X className="w-4 h-4" />;
      case 'processing': return <Camera className="w-4 h-4" />;
      default: return <Camera className="w-4 h-4" />;
    }
  };

  const getStatusBgColor = () => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'processing': return 'bg-luxury-gold text-black animate-pulse';
      default: return 'bg-white/20 text-white/80';
    }
  };

  return (
    <Card className={`transition-all duration-300 ${
      status === 'approved' ? 'border-green-500/50 bg-green-500/10' :
      status === 'rejected' ? 'border-red-500/50 bg-red-500/10' :
      status === 'processing' ? 'border-luxury-gold/50 bg-luxury-gold/10' :
      'bg-white/5 border-white/10'
    }`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-white">
          <div className={`p-2 rounded-full ${getStatusBgColor()}`}>
            {getStatusIcon()}
          </div>
          Verificação por Selfie
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Blocked state - Email not verified */}
        {!isEmailVerified && (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Camera className="w-6 h-6 text-white/40" />
            </div>
            <p className="text-white/60 text-sm font-medium mb-1">
              Etapa bloqueada
            </p>
            <p className="text-white/40 text-xs">
              Complete a verificação por email primeiro
            </p>
          </div>
        )}

        {isEmailVerified && status === 'approved' && (
          <div className="text-center py-4">
            <div className="text-green-400 font-medium flex items-center justify-center gap-2">
              <Check className="w-5 h-5" />
              Selfie verificada!
            </div>
            {verification?.selfie_verified_at && (
              <p className="text-sm text-white/60 mt-1">
                Verificado em {new Date(verification.selfie_verified_at).toLocaleDateString('pt-BR')}
              </p>
            )}
            
            {/* Admin debug feature removed - use admin panel */}
            {false && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-white/40 mb-2">🔧 Admin: Modo Debug</p>
                <Button
                  onClick={retryRejectedSelfie}
                  variant="outline"
                  className="w-full border-luxury-gold/30 text-luxury-gold hover:bg-luxury-gold/10"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Testar Verificação Novamente
                </Button>
              </div>
            )}
          </div>
        )}

        {isEmailVerified && status === 'rejected' && verification?.ai_analysis && (
          <div className="space-y-3">
            {/* Mostrar selfie rejeitada para comparação */}
            {verification.selfie_photo_url && (
              <div className="relative rounded-lg overflow-hidden border border-red-500/20">
                <img
                  src={`${supabase.storage.from('verification-documents').getPublicUrl(verification.selfie_photo_url).data.publicUrl}`}
                  alt="Selfie rejeitada"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                  <span className="bg-red-500/90 text-white text-xs px-2 py-1 rounded font-medium">
                    Selfie Anterior (Rejeitada)
                  </span>
                </div>
                <p className="absolute bottom-2 left-2 right-2 text-center bg-black/80 text-white text-xs p-2 rounded">
                  Veja o que houve para tirar uma nova foto melhor
                </p>
              </div>
            )}
            
            {/* Motivo da rejeição com qualidade */}
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm font-medium mb-2">Motivo da rejeição:</p>
              <p className="text-white/80 text-sm">
                {verification.ai_analysis.reason || 'Não foi possível verificar a selfie.'}
              </p>
              {verification.ai_analysis.qualityScore !== undefined && (
                <p className="text-white/60 text-xs mt-2">
                  Qualidade detectada: {verification.ai_analysis.qualityScore}/10
                </p>
              )}
            </div>
            
            <Button
              onClick={retryRejectedSelfie}
              className="w-full bg-luxury-gold hover:bg-luxury-gold/80 text-black font-medium"
            >
              <Camera className="w-4 h-4 mr-2" />
              Tirar Nova Selfie
            </Button>
          </div>
        )}

        {isEmailVerified && (status === 'pending' || isRetrying) && (
          <div className="space-y-4">
            {/* Video only when no captured image */}
            {!capturedImage && (
              <div className="relative">
                <video
                  ref={videoRef}
                  className="w-full h-64 object-cover rounded-lg bg-black -scale-x-100"
                  playsInline
                  muted
                  autoPlay
                />
                {/* Face guide overlay when capturing */}
                {isCapturing && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="w-40 h-56 border-4 border-luxury-gold rounded-full border-dashed opacity-60" />
                  </div>
                )}
              </div>
            )}

            {/* Camera controls */}
            {!isCapturing && !capturedImage && (
              <Button
                onClick={startCamera}
                disabled={isStarting}
                className="w-full bg-luxury-gold hover:bg-luxury-gold/80 text-black font-medium"
              >
                {isStarting ? 'Iniciando...' : 'Iniciar câmera'}
              </Button>
            )}

            {isCapturing && (
              <div className="flex flex-col gap-3">
                <Button
                  onClick={stopCamera}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={capturePhoto}
                  className="w-full bg-luxury-gold hover:bg-luxury-gold/80 text-black font-medium"
                >
                  Capturar
                </Button>
              </div>
            )}

            {capturedImage && (
              <>
                <img
                  src={capturedImage}
                  alt="Selfie capturada"
                  className="w-full h-64 object-cover rounded-lg"
                />
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => setCapturedImage(null)}
                    className="w-full bg-white text-black hover:bg-white/90 font-medium"
                  >
                    Tirar nova
                  </Button>
                  <Button
                    onClick={submitSelfie}
                    disabled={isProcessing}
                    className="w-full bg-luxury-gold hover:bg-luxury-gold/80 text-black font-medium disabled:opacity-50"
                  >
                    {isProcessing ? 'Enviando...' : 'Enviar Selfie'}
                  </Button>
                </div>
              </>
            )}

            <canvas ref={canvasRef} className="hidden" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SelfieVerificationCard;